import { useState, useCallback, useMemo, useEffect, useRef, useId } from 'react';
import { Box, Typography } from '../../../components/ui';
import { useKnowledgeBase } from '../../../contexts/KnowledgeBaseContext';
import type { KnownErrorFormData } from '../../../types/knowledgeBase';
import './KnownErrorEditor.css';

interface KnownErrorEditorProps {
  content: KnownErrorFormData;
  onChange: (content: KnownErrorFormData) => void;
  errors?: {
    affectedSystems?: string;
    symptoms?: string;
    workaround?: string;
  };
}

export function KnownErrorEditor({ content, onChange, errors = {} }: KnownErrorEditorProps) {
  const { articles } = useKnowledgeBase();
  const [systemInput, setSystemInput] = useState('');
  const [showRelatedPicker, setShowRelatedPicker] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);

  // Generate unique IDs for accessibility
  const baseId = useId();
  const systemsInputId = `${baseId}-systems`;
  const symptomsId = `${baseId}-symptoms`;
  const workaroundId = `${baseId}-workaround`;

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!showRelatedPicker) return;

    function handleClickOutside(event: MouseEvent) {
      if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) {
        setShowRelatedPicker(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showRelatedPicker]);

  // Filter published articles that aren't already linked
  const availableArticles = useMemo(() => {
    return articles.filter(
      (article) => article.isPublished && !content.relatedArticleIds.includes(article.id)
    );
  }, [articles, content.relatedArticleIds]);

  // Resolve related article IDs to full article objects
  const relatedArticles = useMemo(() => {
    return content.relatedArticleIds
      .map((id) => articles.find((article) => article.id === id))
      .filter((article): article is NonNullable<typeof article> => article !== undefined);
  }, [articles, content.relatedArticleIds]);

  const handleFieldChange = useCallback(
    (field: keyof KnownErrorFormData, value: string | string[]) => {
      onChange({ ...content, [field]: value });
    },
    [content, onChange]
  );

  const handleAddSystem = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter' || event.key === ',') {
        event.preventDefault();
        const trimmedSystem = systemInput.trim();
        if (trimmedSystem && !content.affectedSystems.includes(trimmedSystem)) {
          onChange({
            ...content,
            affectedSystems: [...content.affectedSystems, trimmedSystem],
          });
        }
        setSystemInput('');
      }
    },
    [content, onChange, systemInput]
  );

  const handleRemoveSystem = useCallback(
    (systemToRemove: string) => {
      onChange({
        ...content,
        affectedSystems: content.affectedSystems.filter((system) => system !== systemToRemove),
      });
    },
    [content, onChange]
  );

  const handleAddRelatedArticle = useCallback(
    (articleId: string) => {
      onChange({
        ...content,
        relatedArticleIds: [...content.relatedArticleIds, articleId],
      });
      setShowRelatedPicker(false);
    },
    [content, onChange]
  );

  const handleRemoveRelatedArticle = useCallback(
    (articleId: string) => {
      onChange({
        ...content,
        relatedArticleIds: content.relatedArticleIds.filter((id) => id !== articleId),
      });
    },
    [content, onChange]
  );

  const handleTogglePicker = useCallback(() => {
    setShowRelatedPicker((prev) => !prev);
  }, []);

  return (
    <Box className="known-error-editor">
      {/* Affected Systems - First/Top */}
      <Box className="known-error-editor__section">
        <Box className="known-error-editor__section-header">
          <label htmlFor={systemsInputId} className="known-error-editor__section-title">
            Affected Systems
          </label>
          <span className="known-error-editor__required" aria-hidden="true">
            At least one
          </span>
        </Box>
        <Typography variant="body2" className="known-error-editor__description">
          List the systems, applications, or services impacted by this error.
        </Typography>
        <Box
          className="known-error-editor__tags"
          role="group"
          aria-label="Affected systems"
        >
          {content.affectedSystems.map((system) => (
            <span key={system} className="known-error-editor__tag">
              {system}
              <button
                className="known-error-editor__tag-remove"
                onClick={() => handleRemoveSystem(system)}
                type="button"
                aria-label={`Remove ${system} from affected systems`}
              >
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <path
                    d="M3 3l6 6M3 9l6-6"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </span>
          ))}
          <input
            id={systemsInputId}
            type="text"
            className="known-error-editor__tag-input"
            value={systemInput}
            onChange={(e) => setSystemInput(e.target.value)}
            onKeyDown={handleAddSystem}
            placeholder={content.affectedSystems.length === 0 ? 'Type and press Enter...' : 'Add another...'}
            aria-invalid={Boolean(errors.affectedSystems)}
          />
        </Box>
        {errors.affectedSystems && (
          <span className="known-error-editor__error" role="alert">
            {errors.affectedSystems}
          </span>
        )}
      </Box>

      {/* Symptoms */}
      <Box className="known-error-editor__section">
        <Box className="known-error-editor__section-header">
          <label htmlFor={symptomsId} className="known-error-editor__section-title">
            Symptoms
          </label>
          <span className="known-error-editor__required" aria-hidden="true">
            Required
          </span>
        </Box>
        <Typography variant="body2" className="known-error-editor__description">
          Describe how users experience this error. What do they see or encounter?
        </Typography>
        <textarea
          id={symptomsId}
          className="known-error-editor__textarea"
          value={content.symptoms}
          onChange={(e) => handleFieldChange('symptoms', e.target.value)}
          placeholder="Example: Users see a 'Connection timeout' error when trying to access the VPN after 5pm..."
          rows={4}
          aria-required="true"
          aria-invalid={Boolean(errors.symptoms)}
          aria-describedby={errors.symptoms ? `${symptomsId}-error` : undefined}
        />
        {errors.symptoms && (
          <span id={`${symptomsId}-error`} className="known-error-editor__error" role="alert">
            {errors.symptoms}
          </span>
        )}
      </Box>

      {/* Workaround - Main Solution */}
      <Box className="known-error-editor__section known-error-editor__section--workaround">
        <Box className="known-error-editor__section-header">
          <label htmlFor={workaroundId} className="known-error-editor__section-title">
            Workaround / Fix
          </label>
          <span className="known-error-editor__required" aria-hidden="true">
            Required
          </span>
        </Box>
        <Typography variant="body2" className="known-error-editor__description">
          Describe the current fix or workaround for this issue. This is the solution users should follow.
        </Typography>
        <textarea
          id={workaroundId}
          className="known-error-editor__textarea known-error-editor__textarea--main"
          value={content.workaround}
          onChange={(e) => handleFieldChange('workaround', e.target.value)}
          placeholder="Example: To resolve this issue, users should: 1) Close all VPN connections, 2) Clear the DNS cache by running 'ipconfig /flushdns', 3) Reconnect to vpn2.company.com..."
          rows={6}
          aria-required="true"
          aria-invalid={Boolean(errors.workaround)}
          aria-describedby={errors.workaround ? `${workaroundId}-error` : undefined}
        />
        {errors.workaround && (
          <span id={`${workaroundId}-error`} className="known-error-editor__error" role="alert">
            {errors.workaround}
          </span>
        )}
      </Box>

      {/* Related Articles */}
      <Box className="known-error-editor__section">
        <Box className="known-error-editor__section-header">
          <Typography variant="subtitle1" className="known-error-editor__section-title">
            Related Articles
          </Typography>
          <span className="known-error-editor__optional" aria-hidden="true">
            Optional
          </span>
        </Box>
        <Typography variant="body2" className="known-error-editor__description">
          Link to other articles that might help with this issue.
        </Typography>

        {relatedArticles.length > 0 && (
          <Box className="known-error-editor__related-list" role="list">
            {relatedArticles.map((article) => (
              <Box key={article.id} className="known-error-editor__related-item" role="listitem">
                <Box className="known-error-editor__related-info">
                  <span className="known-error-editor__related-title">{article.title}</span>
                  <span className="known-error-editor__related-category">{article.category}</span>
                </Box>
                <button
                  className="known-error-editor__related-remove"
                  onClick={() => handleRemoveRelatedArticle(article.id)}
                  type="button"
                  aria-label={`Remove link to ${article.title}`}
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <path
                      d="M3.5 3.5l7 7M3.5 10.5l7-7"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                  </svg>
                </button>
              </Box>
            ))}
          </Box>
        )}

        <Box ref={pickerRef} className="known-error-editor__related-picker">
          <button
            className="known-error-editor__add-related"
            onClick={handleTogglePicker}
            type="button"
            aria-expanded={showRelatedPicker}
            aria-haspopup="listbox"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                d="M8 3v10M3 8h10"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
            Link Article
          </button>

          {showRelatedPicker && (
            <Box className="known-error-editor__picker-dropdown" role="listbox">
              {availableArticles.length === 0 ? (
                <Box className="known-error-editor__picker-empty">
                  No available articles to link
                </Box>
              ) : (
                availableArticles.slice(0, 10).map((article) => (
                  <button
                    key={article.id}
                    className="known-error-editor__picker-item"
                    onClick={() => handleAddRelatedArticle(article.id)}
                    type="button"
                    role="option"
                  >
                    <span className="known-error-editor__picker-title">{article.title}</span>
                    <span className="known-error-editor__picker-category">{article.category}</span>
                  </button>
                ))
              )}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}
